
import React, { useState }  from 'react';

import { Grid, Typography, Checkbox, FormControl, RadioGroup, FormControlLabel, Radio, TextField } from '@material-ui/core'

import { renderFieldGroupEdit, renderPrimaryPhysicalGroupEdit } from './PassEdit';
import { ColorPicker, ManageTextField } from 'react-frontend-utils'
import Autocomplete from '@material-ui/lab/Autocomplete';

import { DynamicDataType } from '../components/PassField';
import { PPassPreview } from '../components/PPassPreview';
import { BaseFonts, GoogleFonts, ThemeColors } from '../Theme';

/**
 * Designer page for Generic Passes.  The left side is the builder and the right side is the preview.
 * Caller must pass the following props:
 * 
 *  community:  the Community object whose pass fields are being edited
 *  windowWidth: the current window width, to dynamically update as the window changes
 *  onChange: a callback function taking no arguments, that is called when any part of the pass changes (should call forceUpdate to update the preview)
 * 
 */


export function GenericPassBuilder(props) {

    const style = {
        checkbox: {display: 'flex', alignItems: 'center', marginLeft: -10},
        fieldLabel: {display: 'flex', alignItems: 'center'},
        fieldGroup: {border: '1px solid lightGray', borderRadius: 5, padding: 10, marginTop: 20},
        roundedContainer: {
            border: '1px solid #CCCCCC', 
            borderRadius: '4px', 
            padding: 10,
        },
        containerLabel: {
            fontSize: 12, 
            color: 'gray', 
            marginLeft: 5
        }
    };

    const allFonts = [...GoogleFonts, ...BaseFonts];
    const titleFonts = allFonts.sort();

    const community = props.community;
    const [orientationIsVertical, setOrientationIsVertical] = useState(community.gOrientationIsVertical ? "Portrait" : "Landscape");
    const [gColoredField, setGColoredField] = useState(community.gColoredField);
    const [gSecondaryFields, setGSecondaryFields] = useState(community.gSecondaryFields); 
    const [gBackFields, setGBackFields] = useState(community.gBackFields); 
    const [showBarcode, setShowBarcode] = useState(community.gBarcode); 

    const [titleFont, setTitleFont] = useState(community.gTitleFont ? community.gTitleFont : titleFonts[0]); 
    const [titleFontVal, setTitleFontVal] = useState(community.gTitleFont ? community.gTitleFont : ""); 

    const selectTitleFont = (event, newFont) => {
        if (!newFont)
            return;
        setTitleFont(newFont);
        community.gTitleFont = newFont;
        props.onChange();
    }


    const MAX_BACK_FIELDS = 4;
    
    let maxFrontFields = community.gOrientationIsVertical ? 0 : 1;

    if (!community.gBarcode)
        maxFrontFields += community.gOrientationIsVertical ? 1 : 2;

    if (!community.gColoredField)
        maxFrontFields++;
    
    const reducedFrontFields = gSecondaryFields.slice(0, maxFrontFields);  //limit to the amount allowed

    const enableColoredField = (enable) => {
        if (!enable) {
            community.gColoredField= null;
            setGColoredField(null);
        }
        else {
            community.gColoredField = {colorLogic: [], defaultColor: ThemeColors.queuedBlue, defaultValue: "Primary Field Text"};
            setGColoredField(community.gColoredField);
        }
        props.onChange();
    }

    const addSecondaryField = () => {
        community.gSecondaryFields = [...community.gSecondaryFields, {label: "Your Label", dynamicDataType: DynamicDataType.FIXED.enumName}];
        setGSecondaryFields(community.gSecondaryFields);
        props.onChange();
    }

    const removeSecondaryField = () => {
        community.gSecondaryFields = community.gSecondaryFields.slice(0, -1);
        setGSecondaryFields(community.gSecondaryFields);
        props.onChange();
    }

    const addBackField = () => {
        community.gBackFields = [...community.gBackFields, {label: "Your Label", dynamicDataType: DynamicDataType.FIXED.enumName}];
        setGBackFields(community.gBackFields);
        props.onChange();
    }

    const removeBackField = () => {
        community.gBackFields = community.gBackFields.slice(0, -1);
        setGBackFields(community.gBackFields);
        props.onChange();
    }

    const changeFontSize = (event) => {
        community.gTitleFontSize = Number.parseInt(event.target.value);
        props.onChange();
    }

    const changeOrientation = (event) => {
        if (event.target.value === "Portrait")
            community.gOrientationIsVertical = true;
        else
            community.gOrientationIsVertical = false;

        setOrientationIsVertical(event.target.value);
    }

    //Render at 85% of the window when we are below "md" size, otherwise 45%
    let previewWidth = props.windowWidth < 960 ? props.windowWidth * 0.85 : props.windowWidth * 0.45;

    if (previewWidth > 550)  //limit max size
        previewWidth = 550;

    return (


        <Grid container spacing={2}>


            <Grid item xs={12} md={6}>
                
                <ManageTextField label="Mailing Address"
                                         initialValue={community.genericPassMailAddress}
                                         style={{marginBottom: 25, marginTop: 10}}
                                         multiline={3}
                                         json="genericPassMailAddress"  
                                         autoAccept={true}
                                         onFieldChange={(fieldName, value) => {community.genericPassMailAddress = value; props.onChange(); }}/>

                <FormControl component="fieldset">
                    <RadioGroup row name="orientation" value={orientationIsVertical} onChange={changeOrientation}>
                        <FormControlLabel key={1} value="Portrait" control={<Radio color="primary" />} label={"Portrait"} />
                        <FormControlLabel key={2} value="Landscape" control={<Radio color="primary" />} label={"Landscape"} />
                    </RadioGroup>
                </FormControl>  

                <div style={{display: 'flex', gap: 20, alignItems: 'center', marginBottom: 15}}>
                    <div style={style.checkbox}>
                        <Checkbox checked={showBarcode} color='primary' 
                                    onChange={(event) => { const checked = event.target.checked; 
                                                        setShowBarcode(checked);
                                                        community.gBarcode = checked;
                                                        props.onChange(); 
                                                        }}/>                                  
                        <Typography variant='body1' align='left'>Show Barcode</Typography>                        
                    </div> 
                    <div style={{...style.checkbox, display: community.gBarcode ? 'flex' : 'none' }}>
                        <Checkbox checked={community.gBarcodeShowValue} color='primary' disabled={!community.gBarcode} 
                                    onChange={(event) => { community.gBarcodeShowValue = event.target.checked; props.onChange(); }}/>                                  
                        <Typography variant='body1' align='left'>Show Barcode Value</Typography>                        
                    </div> 
                </div>

                <Typography variant='body1' style={style.containerLabel}>Title</Typography>      
                <div style={{...style.roundedContainer, display: 'flex', alignItems: 'center', justifyContent: 'left', gap: '3%'}}>
                    
                    <Autocomplete
                        style={{minWidth: 250}}
                        size='small'
                        disableClearable
                        value={titleFont}
                        onChange={selectTitleFont}
                        inputValue={titleFontVal}
                        onInputChange={(event, newValue) => { setTitleFontVal(newValue) }}
                        options={titleFonts}
                        renderOption={(option) => (
                            <div style={{fontFamily: option, fontSize: '1.2em'}}>{option}</div>
                          )}
                        blurOnSelect
                        renderInput={(params) => <TextField {...params} label="Font" variant="outlined" InputLabelProps={{ shrink: true }} />}
                    />  

                    <ColorPicker initialColor={community.gFontColor} 
                                    disableAlpha={true}
                                    onChange={(color) => {community.gFontColor = color.hex; props.onChange(); }}/>

                    <FormControl component="fieldset">
                        <RadioGroup row name="fontsize" value={community.gTitleFontSize.toString()} onChange={changeFontSize}>
                            <FormControlLabel key={1} value="-1" control={<Radio color="primary" />} label={"Smaller"} />
                            <FormControlLabel key={2} value="0" control={<Radio color="primary" />} label={"Regular"} />
                            <FormControlLabel key={3} value="1" control={<Radio color="primary" />} label={"Large"} />
                            <FormControlLabel key={4} value="2" control={<Radio color="primary" />} label={"Largest"} />
                        </RadioGroup>
                    </FormControl>  

                </div>

                {renderPrimaryPhysicalGroupEdit("Primary", enableColoredField, gColoredField, props.onChange, false)}
                {renderFieldGroupEdit("Secondary", addSecondaryField, removeSecondaryField, reducedFrontFields, maxFrontFields, props.onChange, false)}
                {renderFieldGroupEdit("Back", addBackField, removeBackField, gBackFields, MAX_BACK_FIELDS, props.onChange, false)}

            </Grid>

            <Grid item xs={12} md={6}>
                <div style={{display: 'flex', justifyContent: 'center'}}>
                    <PPassPreview logoImage={null}
                                  passImage={community.logoImage}
                                  extendedName={community.extendedName}
                                  orientationIsVertical={community.gOrientationIsVertical}
                                  secondaryFields={community.gSecondaryFields}
                                  primaryField={community.gColoredField}
                                  enablePatronImage={true}
                                  barcode={community.gBarcode}
                                  barcodeShowValue={community.gBarcodeShowValue}
                                  backFields={community.gBackFields}
                                  titleFontSize={community.gTitleFontSize}
                                  fontColor={community.gFontColor}
                                  titleFont={community.gTitleFont}
                                  previewWidth={previewWidth}
                                  maxFrontFields={maxFrontFields}
                                  refreshEpass={props.onChange}/>
                </div>
            </Grid>

        </Grid>
    );
}